/**
 * impersonation
 *
 * Admin "Impersonate User" — a ContinuumAdmin mints a session token that
 * authenticates AS another user (via the auth.ImpersonateUser RPC) so they can
 * view the app from that user's perspective (e.g. to see the per-job ACL as a
 * non-admin would).
 *
 * Mechanics (confirmed against the hermes framework): identity is the auth token
 * the gateway binds to the mailbox. So "become user Y" = obtain a token bound to
 * Y, swap it into localStorage, and reload — every RPC then authorizes as Y. We
 * reuse the existing /auth/handoff?sess= route as the swap+reload mechanism.
 *
 * The admin's own token is backed up first so "Return to admin" can restore it.
 */

import GlobalClient from "../hermesclient/client";
import { ContentType, RpcFrameType } from "../hermesclient/proto/wsmessages";
import { nameMappingService } from "./NameMappingService";
import { asLogicalMailbox } from "../hermesclient/types";
import { AuthTokenManager } from "./auth-token-manager";

// Per-TAB impersonation info (sessionStorage, NOT shared across tabs). The token
// itself lives in AuthTokenManager's per-tab override; this is just the display
// info for the indicator. Because both are sessionStorage, impersonation takes
// over ONLY the current tab — other tabs keep the shared (admin) identity.
const IMPERSONATION_INFO_KEY = "impersonation_info";

export interface ImpersonateUserResponse {
  sessionToken: string;
  targetUserUid: string;
  targetName: string;
  targetEmail?: string;
  impersonatorUid: string;
  impersonatorName: string;
  expiresAt?: string;
}

/** What we persist so the banner can show "(impersonating Y)" + return safely. */
export interface ImpersonationInfo {
  targetName: string;
  targetUserUid: string;
  impersonatorName: string;
}

/** Call auth.ImpersonateUser to mint a session token for the target user. */
export async function impersonateUserRpc(
  targetUserUid: string,
  reason: string = "",
): Promise<ImpersonateUserResponse> {
  const client = GlobalClient.get();
  const authMailbox = await nameMappingService.resolve(asLogicalMailbox("auth"));
  const request = { targetUserUid, reason };
  const requestBytes = new TextEncoder().encode(JSON.stringify(request));

  const response = await client.rawRpcCall({
    to: authMailbox,
    endPoint: "auth.ImpersonateUser",
    body: requestBytes,
    contentType: ContentType.Json,
  });

  const frameType = response.header?.rpcHeader?.frameType;
  if (frameType === RpcFrameType.ErrorResponse) {
    const errorInfo = response.header?.rpcHeader?.errorInfo;
    throw new Error(
      errorInfo?.message ||
        errorInfo?.errorNode?.frame?.message ||
        "ImpersonateUser RPC failed",
    );
  }
  if (!response.data || response.data.length === 0) {
    throw new Error("ImpersonateUser returned no data");
  }
  const text = new TextDecoder().decode(response.data);
  return JSON.parse(text) as ImpersonateUserResponse;
}

/** True if THIS tab is currently impersonating (has a per-tab override). */
export function isImpersonating(): boolean {
  return AuthTokenManager.getInstance().hasSessionOverride();
}

/** This tab's impersonation info (for the indicator), or null if not impersonating. */
export function getImpersonationInfo(): ImpersonationInfo | null {
  const raw = sessionStorage.getItem(IMPERSONATION_INFO_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as ImpersonationInfo;
  } catch {
    return null;
  }
}

/**
 * Begin impersonating IN THIS TAB ONLY: mint the target's token, set it as this
 * tab's per-tab override (sessionStorage — the shared/admin token is untouched,
 * so other tabs stay as the original identity), then reload so every client
 * re-reads the override and the whole tab acts as the target.
 *
 * Reloads the tab — does not return on success.
 */
export async function beginImpersonation(
  targetUserUid: string,
  reason: string = "",
): Promise<void> {
  const resp = await impersonateUserRpc(targetUserUid, reason);

  const info: ImpersonationInfo = {
    targetName: resp.targetName,
    targetUserUid: resp.targetUserUid,
    impersonatorName: resp.impersonatorName,
  };
  // Per-tab only: the override token + info both go to sessionStorage.
  sessionStorage.setItem(IMPERSONATION_INFO_KEY, JSON.stringify(info));
  AuthTokenManager.getInstance().setSessionOverride(resp.sessionToken);

  // Reload so AuthTokenManager + all hermes clients pick up the override.
  window.location.reload();
}

/**
 * Stop impersonating in THIS tab: drop the per-tab override so getToken() falls
 * back to the shared (original/admin) token, then reload. Other tabs are
 * unaffected (they never had the override).
 */
export function returnToAdmin(): void {
  sessionStorage.removeItem(IMPERSONATION_INFO_KEY);
  AuthTokenManager.getInstance().clearSessionOverride();
  window.location.reload();
}
